import React, { useCallback, useMemo } from "react";
import * as Option from "@utils/Option";

import { useAdminUserClient } from "@contexts/AdminUserClient.context";
import { useAccountClient } from "@contexts/AccountClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import type {
  AdminUserSummary,
  InviteResult,
} from "@ps-bridge/models/clients/adminUsers";
import type { IOts, TaskTs } from "@ps-bridge/interop";
import type { StateRead } from "@ps-bridge/capabilitiesTypes";
import { getPendingEmailValidation } from "@ps-bridge/services/pendingEmailValidation";
import { ok, type Ok } from "@ps-bridge/ok";
import { mkTaskRunner } from "@ps-bridge/capabilities";
import { useRunIO, useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { useOnInit } from "@hooks/effects/useIoEffect";
import { cleanupNone, cleanupSome, useIoEffectWithCleanup } from "@hooks/effects/useIoEffectWithCleanup";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useIoState } from "@utils/useIoState";
import type { AdminUsersKernel, AdminUsersViewProps } from "@ps-bridge/contracts/adminUsers";
import { buildAdminUsersViewProps } from "@ps-bridge/adminUsers.program";
import { type AdminUsersInviteEntry } from "@view/panels/workspace/AdminUsers.panel";
import AdminUsersPagePanel from "@view/panels/workspace/AdminUsersPage.panel";
import { ioEffect } from "@utils/effects/branded";

const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const inviteeRegex = /(?:"?([^"<\n,;]+?)"?\s*<)?([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

type InviteEntry = AdminUsersInviteEntry;

const extractEmails = (raw: string): string[] => {
  const matches = raw.match(emailRegex) ?? [];
  return Array.from(new Set(matches.map((value) => value.toLowerCase())));
};

const extractInviteEntries = (raw: string): InviteEntry[] => {
  const entries = new Map<string, InviteEntry>();
  inviteeRegex.lastIndex = 0;
  let match = inviteeRegex.exec(raw);
  while (match) {
    const namePart = (match[1] ?? "").trim();
    const email = (match[2] ?? "").trim();
    if (!email) {
      continue;
    }
    const key = email.toLowerCase();
    const cleanedName = namePart.replace(/\s+/g, " ").trim();
    const name = cleanedName ? Option.some(cleanedName) : Option.none<string>();
    const rawValue = Option.fold(name, () => email, (value) => `${value} <${email}>`);
    const existing = entries.get(key);
    if (
      !existing
      || (Option.fold(existing.name, () => true, () => false)
        && Option.fold(name, () => false, () => true))
    ) {
      entries.set(key, { raw: rawValue, email, name });
    }
    match = inviteeRegex.exec(raw);
  }
  return Array.from(entries.values());
};

const AdminUsersPage: React.FC = () => {
  const adminUserClient = useAdminUserClient();
  const accountClient = useAccountClient();
  const loading = useLoadingController();
  const runEffect = useRunIO();
  const runTaskWithError = useRunTaskWithError();
  const pendingEmailValidation = getPendingEmailValidation(undefined) as StateRead<boolean>;

  const usersState = useIoState<AdminUserSummary[]>([]);
  const inviteTextState = useIoState("");
  const inviteResultState = useIoOptionState<InviteResult>(Option.none());
  const menuUserIdState = useIoOptionState<string>(Option.none());
  const currentEmailState = useIoOptionState<string>(Option.none());
  const loadErrorState = useIoOptionState<string>(Option.none());
  const seatsUsedState = useIoOptionState<number>(Option.none());
  const seatLimitState = useIoOptionState<number>(Option.none());
  const emailModalUserState = useIoOptionState<AdminUserSummary>(Option.none());
  const emailModalValueState = useIoState("");
  const emailModalErrorState = useIoOptionState<string>(Option.none());
  const emailModalSubmittingState = useIoState(false);

  const { read: usersRead, set: usersSet } = usersState;
  const { read: inviteTextRead, set: inviteTextSet } = inviteTextState;
  const { read: inviteResultRead, setSome: inviteResultSetSome, setMaybe: inviteResultSetMaybe } = inviteResultState;
  const { read: menuUserIdRead, setSome: menuUserIdSetSome, setMaybe: menuUserIdSetMaybe } = menuUserIdState;
  const { read: currentEmailRead, setSome: currentEmailSetSome, setMaybe: currentEmailSetMaybe } = currentEmailState;
  const { read: loadErrorRead, setSome: loadErrorSetSome, setMaybe: loadErrorSetMaybe } = loadErrorState;
  const { read: seatsUsedRead, setSome: seatsUsedSetSome, setMaybe: seatsUsedSetMaybe } = seatsUsedState;
  const { read: seatLimitRead, setSome: seatLimitSetSome, setMaybe: seatLimitSetMaybe } = seatLimitState;
  const { read: emailModalUserRead, setSome: emailModalUserSetSome, setMaybe: emailModalUserSetMaybe } = emailModalUserState;
  const { read: emailModalValueRead, set: emailModalValueSet } = emailModalValueState;
  const { read: emailModalErrorRead, setSome: emailModalErrorSetSome, setMaybe: emailModalErrorSetMaybe } = emailModalErrorState;
  const { read: emailModalSubmittingRead, set: emailModalSubmittingSet } = emailModalSubmittingState;

  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );
  const runPanelIO = useCallback(
    (effect: IOts<Ok>): Ok => {
      runEffect(effect);
      return ok;
    },
    [runEffect],
  );

  const kernel: AdminUsersKernel = useMemo(
    () => ({
      loading,
      adminUserClient,
      accountClient,
      pendingEmailValidation,
      users: { read: usersRead, set: usersSet },
      inviteText: { read: inviteTextRead, set: inviteTextSet },
      inviteResult: { read: inviteResultRead, setSome: inviteResultSetSome, setMaybe: inviteResultSetMaybe },
      menuUserId: { read: menuUserIdRead, setSome: menuUserIdSetSome, setMaybe: menuUserIdSetMaybe },
      currentEmail: { read: currentEmailRead, setSome: currentEmailSetSome, setMaybe: currentEmailSetMaybe },
      loadError: { read: loadErrorRead, setSome: loadErrorSetSome, setMaybe: loadErrorSetMaybe },
      seatsUsed: { read: seatsUsedRead, setSome: seatsUsedSetSome, setMaybe: seatsUsedSetMaybe },
      seatLimit: { read: seatLimitRead, setSome: seatLimitSetSome, setMaybe: seatLimitSetMaybe },
      emailModalUser: { read: emailModalUserRead, setSome: emailModalUserSetSome, setMaybe: emailModalUserSetMaybe },
      emailModalValue: { read: emailModalValueRead, set: emailModalValueSet },
      emailModalError: { read: emailModalErrorRead, setSome: emailModalErrorSetSome, setMaybe: emailModalErrorSetMaybe },
      emailModalSubmitting: { read: emailModalSubmittingRead, set: emailModalSubmittingSet },
      taskRunner: mkTaskRunner(runTaskWithErrorIo),
    }),
    [
      accountClient,
      adminUserClient,
      currentEmailRead,
      currentEmailSetMaybe,
      currentEmailSetSome,
      emailModalErrorRead,
      emailModalErrorSetMaybe,
      emailModalErrorSetSome,
      emailModalSubmittingRead,
      emailModalSubmittingSet,
      emailModalUserRead,
      emailModalUserSetMaybe,
      emailModalUserSetSome,
      emailModalValueRead,
      emailModalValueSet,
      inviteResultRead,
      inviteResultSetMaybe,
      inviteResultSetSome,
      inviteTextRead,
      inviteTextSet,
      loadErrorRead,
      loadErrorSetMaybe,
      loadErrorSetSome,
      loading,
      menuUserIdRead,
      menuUserIdSetMaybe,
      menuUserIdSetSome,
      pendingEmailValidation,
      runTaskWithErrorIo,
      seatLimitRead,
      seatLimitSetMaybe,
      seatLimitSetSome,
      seatsUsedRead,
      seatsUsedSetMaybe,
      seatsUsedSetSome,
      usersRead,
      usersSet,
    ],
  );

  const viewProps: AdminUsersViewProps = useMemo(() => buildAdminUsersViewProps({ kernel }), [kernel]);

  useOnInit(viewProps.onInit);

  const inviteEntries = useMemo(() => extractInviteEntries(viewProps.inviteText), [viewProps.inviteText]);

  const menuListenerSetup = useMemo(
    () =>
      ioEffect(() => {
        if (Option.fold(viewProps.menuUserId, () => true, () => false)) {
          return cleanupNone();
        }
        const handler = () => {
          runEffect(viewProps.onMenuUserIdChange(Option.none()));
        };
        window.addEventListener("click", handler);
        return cleanupSome(ioEffect(() => {
          window.removeEventListener("click", handler);
          return ok;
        }));
      }),
    [runEffect, viewProps],
  );

  useIoEffectWithCleanup(menuListenerSetup);

  const handleEmailModalSubmit = useMemo(
    () =>
      ioEffect(() => {
        if (Option.fold(viewProps.emailModalUser, () => true, () => false)) {
          return ok;
        }
        const emails = extractEmails(viewProps.emailModalValue);
        const nextEmail = emails.length === 1 ? Option.some(emails[0]) : Option.none<string>();
        runEffect(viewProps.onEmailModalSubmit(nextEmail));
        return ok;
      }),
    [runEffect, viewProps],
  );

  return (
    <AdminUsersPagePanel
      pageState="ready"
      viewProps={viewProps}
      inviteEntries={inviteEntries}
      onEmailModalSubmit={handleEmailModalSubmit}
      okValue={ok}
      runIO={runPanelIO}
    />
  );
};

export default AdminUsersPage;
